import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import {
  LandingFeeExceptionCreate,
  useCreateLandingFeeException,
} from "@/queries/landing";
import { PricedFlight } from "@/types/landing";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import {
  ExceptionFormValues,
  LandingExceptionForm,
  exceptionFormSchema,
} from "./LandingExceptionForm"; // Import shared form and schema

// Define the handle interface for external control
export interface CreateLandingExceptionButtonHandle {
  openModal: () => void;
}

// Props to allow initial values to be passed in
interface CreateLandingExceptionButtonProps {
  initialValues?: Partial<ExceptionFormValues>;
  buttonText?: string;
  buttonVariant?:
    | "default"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "destructive";
  buttonSize?: "default" | "sm" | "lg" | "icon";
  className?: string;
  flight?: PricedFlight; // Add flight prop for display
}

export const CreateLandingExceptionButton = forwardRef<
  CreateLandingExceptionButtonHandle,
  CreateLandingExceptionButtonProps
>(
  (
    {
      initialValues = {},
      buttonText = "Create Exception",
      buttonVariant = "default",
      buttonSize = "default",
      className = "",
      flight,
    },
    ref
  ) => {
    const { mutate: createException, isPending: isCreatingException } =
      useCreateLandingFeeException();
    const { toast } = useToast();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    // Create stable reference to initial values to prevent infinite re-renders
    const stableInitialValues = useMemo(
      () => ({
        name: initialValues.name || "",
        description: initialValues.description || "",
        status: initialValues.status || "ACTIVE",
        signatory_status: initialValues.signatory_status || "BOTH",
        fixed_fee_cents: initialValues.fixed_fee_cents || "",
        criteria_type: initialValues.criteria_type || undefined,
        airline_code_iata: initialValues.airline_code_iata || "",
        tail_number: initialValues.tail_number || "",
        min_weight_lbs: initialValues.min_weight_lbs || "",
        max_weight_lbs: initialValues.max_weight_lbs || "",
        effective_from: initialValues.effective_from || null,
        effective_until: initialValues.effective_until || null,
      }),
      [
        initialValues.name,
        initialValues.description,
        initialValues.status,
        initialValues.signatory_status,
        initialValues.fixed_fee_cents,
        initialValues.criteria_type,
        initialValues.airline_code_iata,
        initialValues.tail_number,
        initialValues.min_weight_lbs,
        initialValues.max_weight_lbs,
        initialValues.effective_from,
        initialValues.effective_until,
      ]
    );

    const [shouldResetForm, setShouldResetForm] = useState(false);
    const [lastInitialValues, setLastInitialValues] =
      useState<Partial<ExceptionFormValues> | null>(null);

    const exceptionForm = useForm<ExceptionFormValues>({
      resolver: zodResolver(exceptionFormSchema),
      defaultValues: stableInitialValues,
    });

    // Expose methods to parent components via ref
    useImperativeHandle(ref, () => ({
      openModal: () => {
        setIsCreateModalOpen(true);
      },
    }));

    useEffect(() => {
      if (isCreateModalOpen) {
        const hasPrefilledValues =
          stableInitialValues.airline_code_iata ||
          stableInitialValues.tail_number ||
          (stableInitialValues.name && stableInitialValues.name !== "");

        const initialValuesChanged =
          lastInitialValues &&
          (lastInitialValues.airline_code_iata !==
            stableInitialValues.airline_code_iata ||
            lastInitialValues.tail_number !== stableInitialValues.tail_number);

        if (
          shouldResetForm ||
          (hasPrefilledValues && (!lastInitialValues || initialValuesChanged))
        ) {
          exceptionForm.reset(stableInitialValues);
          setLastInitialValues(stableInitialValues);
          setShouldResetForm(false);
        } else if (!lastInitialValues) {
          setLastInitialValues(stableInitialValues);
        }
      }
    }, [
      isCreateModalOpen,
      shouldResetForm,
      stableInitialValues,
      exceptionForm,
      lastInitialValues,
    ]);

    // --- Adjusted Submit Handler ---
    const onExceptionSubmit = (values: ExceptionFormValues) => {
      // --- Convert validated strings to numbers/null for API ---
      const final_fixed_fee_cents =
        values.fixed_fee_cents && values.fixed_fee_cents.trim() !== ""
          ? Math.round(parseFloat(values.fixed_fee_cents) * 100)
          : null;

      const final_min_weight_lbs =
        values.min_weight_lbs && values.min_weight_lbs.trim() !== ""
          ? parseInt(values.min_weight_lbs)
          : null;
      const final_max_weight_lbs =
        values.max_weight_lbs && values.max_weight_lbs.trim() !== ""
          ? parseInt(values.max_weight_lbs)
          : null;

      let criteria_data: Record<string, any> = {};
      if (values.criteria_type === "airline") {
        criteria_data = { airline_code_iata: values.airline_code_iata?.trim() };
      } else if (values.criteria_type === "weight") {
        if (final_min_weight_lbs !== null) {
          criteria_data.min_weight_lbs = final_min_weight_lbs;
        }
        if (final_max_weight_lbs !== null) {
          criteria_data.max_weight_lbs = final_max_weight_lbs;
        }
      } else if (values.criteria_type === "tail_number") {
        criteria_data = { tail_number: values.tail_number?.trim() };
      }
      // --- End Conversion ---

      const payload: LandingFeeExceptionCreate = {
        name: values.name,
        description: values.description || null,
        status: values.status,
        signatory_status: values.signatory_status,
        fixed_fee_cents: final_fixed_fee_cents, // Use converted value
        criteria_type: values.criteria_type,
        criteria_data: criteria_data as any, // Type assertion for flexible criteria
        effective_from: values.effective_from
          ? format(values.effective_from, "yyyy-MM-dd")
          : null,
        effective_until: values.effective_until
          ? format(values.effective_until, "yyyy-MM-dd")
          : null,
      };

      createException(payload, {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Landing fee exception created.",
          });
          exceptionForm.reset(stableInitialValues);
          setShouldResetForm(true);
          setLastInitialValues(null);
          setIsCreateModalOpen(false);
        },
        onError: (err) => {
          toast({
            variant: "destructive",
            title: "Error creating exception",
            description: err.message,
          });
        },
      });
    };

    return (
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogTrigger asChild>
          <Button
            variant={buttonVariant}
            size={buttonSize}
            className={className}
          >
            {buttonText}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Landing Fee Exception</DialogTitle>
            <DialogDescription>
              Fill in the details for the new exception rule.
            </DialogDescription>
          </DialogHeader>

          {/* Use the shared form component */}
          <div className="py-4">
            <LandingExceptionForm
              form={exceptionForm}
              onSubmit={onExceptionSubmit}
              isLoading={isCreatingException}
              submitButtonText="Create Exception"
              flight={flight}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);

// Quick Exception button for table cells and detail views
interface QuickExceptionButtonProps {
  airlineCodeIata?: string;
  tailNumber?: string;
  flightName?: string;
  buttonSize?: "default" | "sm" | "lg" | "icon";
  className?: string;
  flight?: PricedFlight;
}

export function QuickExceptionButton({
  airlineCodeIata,
  tailNumber,
  flightName,
  buttonSize = "sm",
  className = "",
  flight,
}: QuickExceptionButtonProps) {
  // Calculate initial values based on flight data
  const initialValues: Partial<ExceptionFormValues> = {
    name: flightName ? `Exception for ${flightName}` : "",
    airline_code_iata: airlineCodeIata || "",
    tail_number: tailNumber || "",
    // Not setting criteria_type to allow user to choose
  };

  return (
    <CreateLandingExceptionButton
      initialValues={initialValues}
      buttonText="Quick Exception"
      buttonVariant="outline"
      buttonSize={buttonSize}
      className={className}
      flight={flight}
    />
  );
}
